import { useEffect, useState } from "react";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { usersApi } from "./api/client";
import { BottomNav } from "./components/BottomNav";
import { useAuth } from "./hooks/useAuth";
import { todayLocalDate } from "./lib/date";
import { ChartsPage } from "./pages/ChartsPage";
import { FoodsPage } from "./pages/FoodsPage";
import { HistoryDayPage } from "./pages/HistoryDayPage";
import { HistoryPage } from "./pages/HistoryPage";
import { LoginPage } from "./pages/LoginPage";
import { ScheduledMealLogPage } from "./pages/ScheduledMealLogPage";
import { SetsPage } from "./pages/SetsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TodayPage } from "./pages/TodayPage";

export default function App() {
  const { user, loading, logout, refreshUser } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const showMenuButton = location.pathname === "/";

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="login-screen">
        <p className="muted">読み込み中…</p>
      </div>
    );
  }

  // ログインが必要かどうか
  if (!user && location.pathname !== "/login") {
    return <Navigate to="/login" replace />;
  }
  if (user && location.pathname === "/login") {
    return <Navigate to="/" replace />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <div className="app-shell">
      {showMenuButton ? (
        <button
          type="button"
          className="app-menu-button"
          aria-label="メニューを開く"
          aria-expanded={menuOpen}
          aria-controls="app-menu-panel"
          onClick={() => setMenuOpen((current) => !current)}
        >
          <span />
          <span />
          <span />
        </button>
      ) : null}

      {showMenuButton && menuOpen && (
        <>
          <button
            type="button"
            className="app-menu-backdrop"
            aria-label="メニューを閉じる"
            onClick={() => setMenuOpen(false)}
          />
          <nav
            id="app-menu-panel"
            className="app-menu-panel"
            aria-label="アプリメニュー"
          >
            <div className="app-menu-title">メニュー</div>
            {[
              ["/", "今日の記録"],
              ["/meal-logs/scheduled", "指定日の食事を登録"],
              ["/charts", "グラフ"],
              ["/history", "履歴"],
              ["/foods", "食品"],
              ["/sets", "セット"],
              ["/settings", "設定"],
            ].map(([path, label]) => (
              <NavLink
                key={path}
                to={path}
                end={path === "/"}
                className="app-menu-link"
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </>
      )}

      {!user.target_configured && (
        <TargetSetupDialog
          onSaved={() => refreshUser(todayLocalDate())}
          initialTarget={user.daily_target_kcal}
        />
      )}

      <Routes>
        <Route path="/" element={<TodayPage user={user} />} />
        <Route
          path="/meal-logs/scheduled"
          element={<ScheduledMealLogPage user={user} />}
        />
        <Route path="/charts" element={<ChartsPage user={user} />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/history/:date" element={<HistoryDayPage user={user} />} />
        <Route path="/foods" element={<FoodsPage />} />
        <Route path="/sets" element={<SetsPage />} />
        <Route
          path="/settings"
          element={
            <SettingsPage
              user={user}
              onLogout={logout}
              onUserChanged={refreshUser}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
    </div>
  );
}

function TargetSetupDialog({
  initialTarget,
  onSaved,
}: {
  initialTarget: number;
  onSaved: () => Promise<unknown>;
}) {
  const [target, setTarget] = useState(String(initialTarget || 2600));
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Number(target) <= 0) return;
    setSaving(true);
    try {
      await usersApi.update({
        daily_target_kcal: Number(target),
        target_date: todayLocalDate(),
      });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="target-setup-title"
      >
        <h2 id="target-setup-title" className="modal-title">
          目標摂取カロリーを設定
        </h2>
        <p className="muted" style={{ marginTop: 0 }}>
          初回設定です。今日の目標を保存すると、以後はその日の値が日別に残ります。
        </p>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="initial-target">1日の目標カロリー (kcal)</label>
            <input
              id="initial-target"
              inputMode="numeric"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </div>
          <button
            className="btn-primary"
            disabled={saving || Number(target) <= 0}
          >
            {saving ? "保存中…" : "設定して始める"}
          </button>
        </form>
      </div>
    </div>
  );
}
